import { useState, useEffect, useRef, useCallback } from "react";
import { salesAPI, dashboardAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Maximize, Minimize, RefreshCw, FlipHorizontal2 } from "lucide-react";

const RealTime = () => {
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [displayRevenue, setDisplayRevenue] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMirrored, setIsMirrored] = useState(false);
  const [showCoinAnimation, setShowCoinAnimation] = useState(false);
  const [lastAmount, setLastAmount] = useState(0);
  const [includesWeekend, setIncludesWeekend] = useState(false);
  const containerRef = useRef(null);
  const previousRevenueRef = useRef(0);

  // Fetch current data
  const fetchData = useCallback(async () => {
    try {
      const res = await dashboardAPI.getStats();
      const newRevenue = res.data.today_revenue || 0;
      setIncludesWeekend(res.data.includes_weekend || false);
      
      // Check if revenue increased
      if (newRevenue > previousRevenueRef.current && previousRevenueRef.current > 0) {
        const diff = newRevenue - previousRevenueRef.current;
        setLastAmount(diff);
        setShowCoinAnimation(true);
        setTimeout(() => setShowCoinAnimation(false), 3000);
      }
      
      previousRevenueRef.current = newRevenue;
      setTodayRevenue(newRevenue);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  // Initial load and polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  // Animated counter
  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const stepTime = duration / steps;
    const increment = (todayRevenue - displayRevenue) / steps;
    
    if (Math.abs(todayRevenue - displayRevenue) < 0.01) return;
    
    let current = displayRevenue;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        setDisplayRevenue(todayRevenue);
        clearInterval(timer);
      } else {
        setDisplayRevenue(current);
      }
    }, stepTime);
    
    return () => clearInterval(timer);
  }, [todayRevenue]);

  // Fullscreen handling - using document.documentElement for better compatibility
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        // Enter fullscreen
        const elem = containerRef.current;
        if (elem) {
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if (elem.webkitRequestFullscreen) {
            await elem.webkitRequestFullscreen();
          } else if (elem.mozRequestFullScreen) {
            await elem.mozRequestFullScreen();
          } else if (elem.msRequestFullscreen) {
            await elem.msRequestFullscreen();
          }
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (err) {
      console.error("Fullscreen error:", err);
      toast.error("Impossibile attivare schermo intero. Prova con F11.");
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  return (
    <div 
      ref={containerRef}
      data-testid="realtime-page" 
      className={`relative overflow-hidden bg-black ${
        isFullscreen 
          ? 'fixed inset-0 z-[9999]' 
          : 'min-h-[80vh] rounded-2xl'
      }`}
      style={{
        background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #0A0A0A 100%)',
      }}
    >
      {/* Inner wrapper for mirror transform */}
      <div 
        className="w-full h-full"
        style={{ transform: isMirrored ? 'scaleX(-1)' : 'none' }}
      >
        {/* Watermark Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
          <div className="text-center opacity-[0.03]">
            <h1 className="font-serif text-[20vw] text-white leading-none tracking-tight">
              Equivalencia
            </h1>
            <p className="text-[3vw] text-white tracking-[0.5em] uppercase mt-4">
              Equipe
            </p>
          </div>
        </div>

        {/* Animated particles/coins */}
        {showCoinAnimation && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-coin-fall"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${2 + Math.random()}s`,
                }}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#C9A227] shadow-lg shadow-amber-500/30 flex items-center justify-center text-xs font-bold text-black">
                  €
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Glow effect when money comes in */}
        {showCoinAnimation && (
          <div className="absolute inset-0 bg-gradient-radial from-amber-500/10 via-transparent to-transparent animate-pulse pointer-events-none" />
        )}

        {/* Main Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[80vh] h-full px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-[#C9A227] text-sm md:text-base tracking-[0.4em] uppercase font-medium mb-2">
              {includesWeekend ? 'Fatturato Weekend + Lunedì' : 'Fatturato Oggi'}
            </p>
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#C9A227] to-transparent mx-auto" />
            {includesWeekend && (
              <p className="text-white/40 text-xs mt-2 tracking-wider">
                Sabato + Domenica + Oggi
              </p>
            )}
          </div>

          {/* Main Counter */}
          <div className="text-center">
            <h1 
              className="font-serif text-6xl sm:text-8xl md:text-9xl lg:text-[12rem] xl:text-[16rem] text-white font-bold tracking-tight leading-none tabular-nums"
              style={{
                textShadow: showCoinAnimation 
                  ? '0 0 80px rgba(201, 162, 39, 0.5), 0 0 120px rgba(201, 162, 39, 0.3)' 
                  : '0 0 40px rgba(201, 162, 39, 0.2)',
                transition: 'text-shadow 0.5s ease',
              }}
            >
              {formatCurrency(displayRevenue)}
            </h1>
          </div>

          {/* New order notification */}
          {showCoinAnimation && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 animate-bounce">
              <div className="bg-gradient-to-r from-[#C9A227] to-[#FFD700] text-black px-8 py-4 rounded-full shadow-2xl shadow-amber-500/30">
                <p className="text-lg md:text-2xl font-bold">
                  + {formatCurrency(lastAmount)}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
            <p className="text-white/30 text-xs md:text-sm tracking-wider">
              Aggiornamento automatico ogni 5 secondi
            </p>
          </div>
        </div>
      </div>

      {/* Controls - outside mirror wrapper so they stay readable */}
      <div 
        className="absolute top-6 right-6 flex items-center gap-3 z-20"
        style={{ transform: isMirrored ? 'scaleX(-1)' : 'none' }}
      >
        <Button
          onClick={() => setIsMirrored(!isMirrored)}
          variant="outline"
          size="sm"
          data-testid="mirror-btn"
          className={`border-white/20 text-white hover:bg-white/20 rounded-full ${isMirrored ? 'bg-[#C9A227]/30' : 'bg-white/10'}`}
          title="Specchia schermo"
        >
          <FlipHorizontal2 className="w-5 h-5" />
        </Button>
        <Button
          onClick={fetchData}
          variant="ghost"
          size="sm"
          className="text-white/50 hover:text-white hover:bg-white/10 rounded-full"
        >
          <RefreshCw className="w-5 h-5" />
        </Button>
        <Button
          onClick={toggleFullscreen}
          variant="ghost"
          size="sm"
          data-testid="fullscreen-btn"
          className="text-white/50 hover:text-white hover:bg-white/10 rounded-full"
        >
          {isFullscreen ? (
            <Minimize className="w-5 h-5" />
          ) : (
            <Maximize className="w-5 h-5" />
          )}
        </Button>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes coin-fall {
          0% {
            transform: translateY(-100px) rotate(0deg) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
            transform: translateY(0) rotate(45deg) scale(1);
          }
          100% {
            transform: translateY(100vh) rotate(720deg) scale(0.5);
            opacity: 0;
          }
        }
        .animate-coin-fall {
          animation: coin-fall ease-out forwards;
        }
        @keyframes bg-gradient-radial {
          from { background: radial-gradient(circle at center, rgba(201, 162, 39, 0.15), transparent 70%); }
        }
      `}</style>
    </div>
  );
};

export default RealTime;
